import { Button, Container, Grid, Paper, Tab, Tabs, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AccessTime, ChevronRight } from "@material-ui/icons";
import { App, Events, Workouts } from "app-interfaces";
import { addYears, format } from "date-fns";
// Workouts Listings w/ Search filter
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { useStateValue } from "../../config/AppContext";
import CatalogClass from "../../models/Catalog";
import { adjustApiDateTimezone } from "../../server-api";
import { EventType, TemplateProps } from "../../types/StaticTypes";
import { formatDate } from "../../utils/time-utils";
import { SelectClientsModal } from "../Modals";
import LoadingSpinnerComponent from "../Utilities/LoadingSpinner";
import SearchFilter from "../Utilities/SearchFilter";
import TabPanel from "../Utilities/TabPanel";

const useStyles = makeStyles((theme) => ({
  searchFilter: {
    margin: "15px 0",
  },
  container: {
    "& .MuiTabs-indicator": {
      display: "none",
    },
    "& .MuiTabs-centered": {
      "& .MuiTab-root": {
        width: "50%",
        height: 42,
        border: "1px solid var(--text-primary)",
        borderRadius: 4,
        opacity: 1,
        "&:first-child": {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        },
        "&:last-child": {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        },
        "& .MuiTab-wrapper": {
          fontSize: 14,
          fontWeight: 500,
          textTransform: "none",
          color: "var(--text-primary)",
          [theme.breakpoints.up("md")]: {
            fontSize: 16,
          },
        },
        "&.Mui-selected": {
          backgroundColor: "var(--text-primary)",
          "& .MuiTab-wrapper": {
            color: "#ffffff",
          },
        },
      },
    },
  },
  list: {
    marginTop: "15px",
  },
  item: {
    padding: theme.spacing(1),
    "& .MuiPaper-root": {
      cursor: "pointer",
      padding: "15px 12px",
      "& h5": {
        fontSize: 16,
        marginBottom: 5,
        width: "60%",
        textTransform: "capitalize",
        color: "#2F364A",
      },
      "& svg": {
        marginTop: -28,
        display: "inline-block",
        float: "right",
        fill: "var(--heading)",
      },
      "& .MuiTypography-body1": {
        display: "inline-block",
        fontSize: 14,
        color: "var(--heading)",
        [theme.breakpoints.down("md")]: {
          display: "block",
        },
      },
      "& .MuiTypography-body2": {
        fontSize: 14,
        color: "var(--heading)",
        "& svg": {
          float: "none",
          fill: "#000000",
          marginTop: -2,
          marginRight: 5,
          height: 16,
          width: 16,
          [theme.breakpoints.down("md")]: {
            marginLeft: 0,
          },
        },
      },
    },
  },
  tabLabel: {
    whiteSpace: "nowrap",
    fontSize: "0",
  },
}));

type ListingsProps = {
  children?: any;
  /**
   * This just says whether the context the component is rendered from
   * is a session listing or sth session related. This is used internally
   * to switch between using handleSubmit or the setShow* functions
   * in the handleClick handler.
   *
   * We should be able to control this externally instead to simplify logic.
   */
  hasSession: boolean;

  /* TODO: replace these three handlers with a single handler def */
  handleSubmit: (params: TemplateProps) => void;
  setShowAddWorkout(id: string): void;
  setShowWorkout(id: string): void;
};

function findAllWorkouts(
  _events: Events.ResponseServerEvent<Events.SessionFullThatUsedToBePreview>[]
) {
  return _events.reduce((accum, event) => {
    const workoutAlreadyAdded = accum.findIndex((el) => el.id === event.session!.workout!.id) > -1;
    if (!workoutAlreadyAdded) {
      const newVar = {
        ...event.session!.workout!,
        start: event.start, // TODO: typing. why add this extra field?
        end: event.end, // TODO: typing. why add this extra field?
      };
      return [...accum, newVar];
    } else {
      return accum;
    }
  }, [] as Workouts.IWorkout[]);
}

type WorkoutsProps = {
  workouts: Workouts.IWorkout[];
  handleClick: (e: React.MouseEvent<HTMLElement>) => void;
};

const WorkoutsList: React.FC<WorkoutsProps> = ({ workouts, handleClick }) => {
  const classes = useStyles();
  return (
    <Grid container spacing={3} className={classes.list}>
      {workouts.map((workout: Workouts.IWorkout, i: number) => (
        <WorkoutItem key={i} workout={workout} handleClick={handleClick} />
      ))}
    </Grid>
  );
};

type WorkoutItemProps = {
  workout: Workouts.IWorkout;
  handleClick: (e: React.MouseEvent<HTMLElement>) => void;
};

const localFormatDate = (date: Date) => {
  return format(adjustApiDateTimezone(date), "dd.MM.yyyy");
};

function WorkoutItem({ workout, handleClick }: WorkoutItemProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { start } = workout;

  return (
    <Grid item xs={6} id={workout.id} className={classes.item} onClick={handleClick}>
      <Paper elevation={1}>
        <Typography variant="h5" noWrap>
          {workout.name}
        </Typography>
        <ChevronRight />
        <Typography variant="body1">
          {workout.exercise_super_set.length} {t("exercises")}
        </Typography>
        <Typography variant="body2">{start && localFormatDate(new Date(start))}</Typography>
        <Typography variant="body2">
          <AccessTime />
          {workout.duration} {t("mins")}
        </Typography>
      </Paper>
    </Grid>
  );
}

export default function WorkoutsListings({
  hasSession,
  handleSubmit,
  setShowAddWorkout,
  setShowWorkout,
  children,
}: ListingsProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const { catalog, clients, Methods } = useStateValue() as {
    catalog: CatalogClass;
    clients: App.State["clients"];
    Methods: App.Methods;
  };

  const [tabIndex, setTabIndex] = React.useState(0);
  const [filtered, setFiltered] = React.useState<Workouts.IWorkout[]>([]);
  const [clientSelect, showClientSelect] = React.useState(false);
  const [clientName, setClientName] = React.useState<string>();
  const [clientID, setClientID] = React.useState<string>();
  const [clientWorkouts, setClientWorkouts] = React.useState<Workouts.IWorkout[]>([]);
  const [clientFiltered, setClientFiltered] = React.useState<Workouts.IWorkout[]>([]);

  // I think this is also used
  const hasSelector = true; // TODO: figure out where this is supposed to come from

  // digs out the id of the workout from the clicked element
  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    const targetId = e.currentTarget.id;

    if (hasSession) {
      const workout = catalog.Workouts.find((el) => el.id === targetId);
      const clientWorkout = clientWorkouts.find((el) => el.id === targetId);
      if (workout) return handleSubmit({ workout });
      if (clientWorkout) return handleSubmit({ workout: clientWorkout });
    }
    if (window.innerWidth > 700) {
      return hasSelector ? setShowAddWorkout(targetId) : setShowWorkout(targetId);
    }
    return hasSelector
      ? history.push(`workouts/add/${targetId}`)
      : history.push(`workouts/${targetId}`);
  };

  const handleFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!catalog) return;
    const inputIsEmpty = e.currentTarget.value === "";
    if (inputIsEmpty) {
      setClientFiltered([]);
      setFiltered([]);
      return;
    }

    const filter = new RegExp(e.currentTarget.value.trim(), "i");
    if (clientWorkouts) {
      const filteredClientWorkouts = clientWorkouts.filter((workout) => filter.test(workout.name));
      setClientFiltered(filteredClientWorkouts);
    }
    const filteredWorkouts = catalog.Workouts.filter((workout) => filter.test(workout.name));
    return setFiltered(filteredWorkouts);
  };
  const handleClients = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const clientsInput = formData.get("clients");
    const clients =
      clientsInput && typeof clientsInput === "string" ? JSON.parse(clientsInput) : [];
    if (clients.length === 0) setClientID(undefined);
    else setClientID(clients[0]);
    showClientSelect(false);
    return setClientWorkouts([]);
  };

  const handleTabs = (e: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  React.useEffect(() => {
    if (!hasSelector) return;

    const now = new Date();

    // Get all client workouts including the ones scheduled for the future.
    // Set the "to" date 1 year ahead to make sure all the future events are included.
    const toDate = addYears(now, 1);

    const params = {
      to: formatDate(toDate, 3),
      types: [EventType.Session],
      has_workout: true,
      limit: 1000,
      customer_id: clientID,
    };
    if (!clientWorkouts.length) {
      Methods.getEvents(params).then((response) => {
        setClientWorkouts(findAllWorkouts(response));
      });
    }
  }, [clientID, clientWorkouts, hasSelector, Methods]);

  React.useEffect(() => {
    if (clients && clientID) {
      const currentClient = clients.find((el) => el.id === clientID);
      if (currentClient && currentClient.first_name !== clientName)
        return setClientName(currentClient.first_name);
    }
  }, [clients, clientID, clientName]);

  const workouts = filtered.length ? filtered : catalog.Workouts;

  const clientWorkoutsWithFiltering = clientFiltered.length ? clientFiltered : clientWorkouts;

  if (!clientWorkouts) return <LoadingSpinnerComponent />;

  return (
    <Container id="workouts-listings" className={classes.container}>
      <SearchFilter
        placeholder="Workout"
        onChange={handleFilter}
        className={classes.searchFilter}
      />
      {!hasSelector ? (
        <>
          <WorkoutsList handleClick={handleClick} workouts={workouts} />
          {children}
        </>
      ) : (
        <>
          <Tabs value={tabIndex} onChange={handleTabs} centered>
            <Tab label={t("All workouts")} />
            <Tab label={t("Client workouts")} className={classes.tabLabel} />
          </Tabs>
          <TabPanel value={tabIndex} index={0}>
            <WorkoutsList handleClick={handleClick} workouts={workouts} />
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <Button
              variant="contained"
              onClick={() => showClientSelect(true)}
              style={{
                textTransform: "none",
                display: "flex",
                margin: "15px auto",
                backgroundColor: "var(--text-primary)",
                color: "#ffffff",
              }}
            >
              {t("Change client")}: {clients && clientID && clientName ? clientName : t("All")}
            </Button>
            <WorkoutsList handleClick={handleClick} workouts={clientWorkoutsWithFiltering} />
            <SelectClientsModal
              show={clientSelect}
              onHide={() => showClientSelect(false)}
              onSubmit={handleClients}
              selected={clientID ? [clientID] : []}
              single
            />
          </TabPanel>
        </>
      )}
    </Container>
  );
}
